import React from 'react';
import { IUserCreate } from '../models/User';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { Api } from '@/lib/api';

import { QueryKeys } from '@/constants/queryKeys';

import { UserApi } from '../api';
import {
  userCreateSchema,
  userPasswordSchema,
  userPermissionsSchema,
  userRolePermissionSchema,
  userRolesSchema,
  userUpdateSchema,
  userBranchSchema,
  userCompanySchema,
} from '../schemas';

export const useUserListQuery = () => {
  const query = useQuery({
    queryKey: [QueryKeys.users],
    queryFn: () => UserApi.listUsers(),
    select: (response) => response.data.data,
  });

  return query;
};

export const useUserListWithAdminQuery = () => {
  const query = useQuery({
    queryKey: [QueryKeys.users],
    queryFn: () => UserApi.listUsersWithAdmin(),
    select: (response) => response.data.data,
  });

  return query;
};

export function useUserQuery(userId?: number | null, retry?: boolean) {
  return useQuery({
    queryKey: [QueryKeys.users, userId],
    queryFn: () => UserApi.getUser(userId || '0'),
    select: (response) => response.data.data,
    enabled: !!userId,
    retry,
  });
}

export function useUserSelfQuery() {
  return useQuery({
    queryKey: [QueryKeys.users, 'me'],
    queryFn: () => UserApi.getCurrentUser(),
    select: (response) => response.data.data,
    enabled: !!Api.hasToken,
    retry: false,
  });
}

export const useUserUpdateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: number;
      data: z.infer<typeof userUpdateSchema>;
    }) => UserApi.updateUser(userId, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users', response.data.data.id] });
    },
  });
};

export const useUserCreateMutation = () => {
  const queryClient = useQueryClient(); 
  return useMutation({
    mutationFn: ({confirmPassword, ...data}: IUserCreate) =>
      UserApi.createUser({...data, confirmPassword}),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};


export const useUserRolePermissionMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: number;
      data: z.infer<typeof userRolePermissionSchema>;
    }) => UserApi.updateUser(userId, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users', response.data.data.id] });
    },
  });
};

export const useUserRolesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: number;
      data: z.infer<typeof userRolesSchema>;
    }) => UserApi.updateUser(userId, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users', response.data.data.id] });
    },
  });
};

export const useUserPermissionsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: number;
      data: z.infer<typeof userPermissionsSchema>;
    }) => UserApi.updateUser(userId, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users', response.data.data.id] });
    },
  });
};

export const useUserCompanyMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: number;
      data: z.infer<typeof userCompanySchema>;
    }) => UserApi.updateUser(userId, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useUserBranchMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: number;
      data: z.infer<typeof userBranchSchema>;
    }) => UserApi.updateUser(userId, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useUserPasswordMutation = () => {
  return useMutation({
    mutationFn: ({
      userId,
      data: { password },
    }: {
      userId: number;
      data: z.infer<typeof userPasswordSchema>;
    }) => UserApi.updateUser(userId, { password }),
  });
};

export const useUserDeleteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: number) => UserApi.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};
